import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FilePdf, Trash, Plus, UploadSimple, MagnifyingGlass, X, CheckCircle, CircleNotch, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminHeaders } from "@/lib/adminAuth";
import ProductManager from "@/components/admin/ProductManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Guess the most likely resource type from a filename
const guessResourceType = (filename, types) => {
  const n = (filename || "").toLowerCase();
  if (n.includes("teacher") || n.includes("guide") || n.includes("tg")) return "Teacher's Guide";
  if (n.includes("answer") || n.includes("key")) return "Answer Key";
  if (n.includes("workbook") || n.includes("student") || n.includes("sw")) return "Worksheet";
  if (n.includes("assessment") || n.includes("test") || n.includes("quiz")) return "Assessment";
  if (n.includes("plan") || n.includes("lesson")) return "Lesson Plan";
  if (n.includes("reference") || n.includes("chart")) return "Reference";
  return types?.[0] || "Worksheet";
};

// Turn "my-file-name_v2.pdf" into "My File Name V2"
const filenameToTitle = (filename) => {
  return (filename || "")
    .replace(/\.pdf$/i, "")
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const PdfManager = () => {
  const [pdfs, setPdfs] = useState([]);
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ topics: [], grades: [], types: [] });
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);

  const fetchPdfs = async () => {
    try {
      const res = await axios.get(`${API}/admin/pdfs`, { headers: adminHeaders() });
      setPdfs(res.data || []);
    } catch (e) {
      toast.error("Failed to load PDFs");
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: adminHeaders() });
      setProducts(res.data || []);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchPdfs();
    fetchProducts();
    axios.get(`${API}/pdfs/meta`).then((r) => setMeta(r.data));
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this PDF? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/pdfs/${id}`, { headers: adminHeaders() });
      toast.success("Deleted");
      fetchPdfs();
      fetchProducts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onAssign = async (pdfId, productId) => {
    try {
      await axios.patch(
        `${API}/admin/pdfs/${pdfId}/assign`,
        { product_id: productId || null },
        { headers: adminHeaders() }
      );
      fetchPdfs();
      fetchProducts();
    } catch {
      toast.error("Assign failed");
    }
  };

  const filtered = pdfs.filter(
    (p) =>
      !search ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.topic.toLowerCase().includes(search.toLowerCase())
  );

  const renderGrades = (p) => {
    const list = Array.isArray(p.grades) && p.grades.length ? p.grades : (p.grade ? [p.grade] : []);
    if (list.length === 0) return null;
    return (
      <span className="text-xs font-nunito font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
        {list.length === 1 ? `Grade ${list[0]}` : `Grades ${list.join(", ")}`}
      </span>
    );
  };

  return (
    <div>
      <ProductManager
        products={products}
        meta={meta}
        onChange={() => {
          fetchProducts();
          fetchPdfs();
        }}
      />

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-fredoka font-bold text-2xl text-foreground">PDF Vault</h1>
          <p className="font-nunito text-muted-foreground text-sm">
            {pdfs.length} resource{pdfs.length === 1 ? "" : "s"} • Upload multiple PDFs at once
          </p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          className="bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-full"
          data-testid="admin-add-pdf-btn"
        >
          <Plus size={18} weight="bold" className="mr-2" />
          Upload PDFs
        </Button>
      </div>

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by title or topic..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 font-nunito border-2 border-foreground rounded-full"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-foreground/30 rounded-2xl p-12 text-center">
          <FilePdf size={56} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="font-nunito text-muted-foreground">No PDFs yet. Upload your first ones!</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((p) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border-2 border-foreground rounded-xl shadow-brutal-sm p-4 flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-red-50 border-2 border-foreground rounded-lg flex items-center justify-center shrink-0">
                <FilePdf size={26} weight="duotone" className="text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-fredoka font-bold text-foreground truncate">{p.title}</p>
                <div className="flex gap-2 mt-1 flex-wrap items-center">
                  <span className="text-xs font-nunito font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {p.topic}
                  </span>
                  {renderGrades(p)}
                  {p.resource_type && (
                    <span className="text-xs font-nunito font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      {p.resource_type}
                    </span>
                  )}
                  <span className="text-xs font-nunito text-muted-foreground">
                    {(p.size / 1024).toFixed(0)} KB • {p.download_count || 0} downloads
                  </span>
                  <div className="flex items-center gap-1 ml-auto">
                    <Package size={14} className="text-muted-foreground" />
                    <select
                      value={p.product_id || ""}
                      onChange={(e) => onAssign(p.id, e.target.value)}
                      className="text-xs font-nunito border border-foreground/30 rounded-md px-2 py-0.5 bg-white"
                      data-testid={`admin-pdf-product-${p.id}`}
                      aria-label="Attach to product"
                    >
                      <option value="">— Standalone —</option>
                      {products.map((prod) => (
                        <option key={prod.id} value={prod.id}>{prod.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDelete(p.id)}
                className="font-nunito border-2 border-foreground rounded-lg"
                data-testid={`admin-delete-pdf-${p.id}`}
              >
                <Trash size={16} />
              </Button>
            </motion.div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          topics={meta.topics}
          grades={meta.grades}
          types={meta.types}
          products={products}
          onClose={() => setShowUpload(false)}
          onDone={() => {
            setShowUpload(false);
            fetchPdfs();
            fetchProducts();
          }}
        />
      )}
    </div>
  );
};

const UploadModal = ({ topics, grades, types, products, onClose, onDone }) => {
  const fileRef = useRef(null);
  // rows: [{ file, title, resource_type, status: 'pending'|'uploading'|'done'|'error', error? }]
  const [rows, setRows] = useState([]);
  const [shared, setShared] = useState({
    topic: topics[0] || "",
    grades: ["K"],
    description: "",
    product_id: "",
  });
  const [uploading, setUploading] = useState(false);

  const addFiles = (files) => {
    const arr = Array.from(files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (!arr.length) return;
    const next = arr.map((file) => ({
      file,
      title: filenameToTitle(file.name),
      resource_type: guessResourceType(file.name, types),
      status: "pending",
    }));
    setRows((prev) => [...prev, ...next]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleGrade = (g) => {
    setShared((prev) => {
      const has = prev.grades.includes(g);
      const grades = has ? prev.grades.filter((x) => x !== g) : [...prev.grades, g];
      return { ...prev, grades };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rows.length) {
      toast.error("Add at least one PDF");
      return;
    }
    if (rows.some((r) => !r.title.trim())) {
      toast.error("Every file needs a title");
      return;
    }
    if (!shared.grades.length) {
      toast.error("Pick at least one grade");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    // Upload sequentially so kids' server load stays predictable
    for (let i = 0; i < rows.length; i++) {
      // Mark uploading
      setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: "uploading" } : r)));

      const row = rows[i];
      const fd = new FormData();
      fd.append("file", row.file);
      fd.append("title", row.title);
      fd.append("topic", shared.topic);
      fd.append("grades", shared.grades.join(","));
      fd.append("resource_type", row.resource_type);
      fd.append("description", shared.description);
      if (shared.product_id) fd.append("product_id", shared.product_id);

      try {
        await axios.post(`${API}/admin/pdfs/upload`, fd, {
          headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
        });
        successCount++;
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: "done" } : r)));
      } catch (err) {
        failCount++;
        const msg = err.response?.data?.detail || "Upload failed";
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: "error", error: msg } : r)));
      }
    }

    setUploading(false);

    if (successCount) toast.success(`${successCount} PDF${successCount === 1 ? "" : "s"} uploaded`);
    if (failCount) toast.error(`${failCount} failed — check rows`);

    if (failCount === 0) {
      // Small pause so user can see checkmarks
      setTimeout(onDone, 500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-foreground rounded-2xl shadow-brutal max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        data-testid="admin-upload-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fredoka font-bold text-xl text-foreground">Upload PDFs</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-muted"
            data-testid="admin-upload-close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Multi-file dropzone */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(e.dataTransfer.files);
            }}
            className="border-2 border-dashed border-foreground/40 rounded-xl p-6 text-center cursor-pointer hover:bg-muted/30"
            data-testid="admin-pdf-dropzone"
          >
            <UploadSimple size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="font-nunito text-sm text-foreground">
              Click or drag & drop <strong>multiple PDFs</strong> (max 25 MB each)
            </p>
            <p className="font-nunito text-xs text-muted-foreground mt-1">
              Filenames auto-fill title + resource type — you can edit each row below
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={(e) => addFiles(e.target.files)}
              className="hidden"
              data-testid="admin-pdf-file-input"
            />
          </div>

          {/* File rows */}
          {rows.length > 0 && (
            <div className="space-y-2 border-2 border-foreground/10 rounded-xl p-3 bg-muted/20">
              <p className="font-nunito font-bold text-sm text-foreground">
                {rows.length} file{rows.length === 1 ? "" : "s"} to upload
              </p>
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className="bg-white border-2 border-foreground/20 rounded-lg p-3 flex items-center gap-2"
                  data-testid={`admin-pdf-row-${idx}`}
                >
                  <FilePdf size={20} weight="duotone" className="text-red-500 shrink-0" />
                  <Input
                    placeholder="Title"
                    value={row.title}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, title: v } : r)));
                    }}
                    disabled={uploading || row.status === "done"}
                    className="font-nunito border border-foreground/30 rounded-md text-sm flex-1 min-w-0"
                    data-testid={`admin-pdf-title-${idx}`}
                  />
                  <select
                    value={row.resource_type}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, resource_type: v } : r)));
                    }}
                    disabled={uploading || row.status === "done"}
                    className="font-nunito border border-foreground/30 rounded-md px-2 py-1 bg-white text-sm shrink-0"
                    data-testid={`admin-pdf-type-${idx}`}
                  >
                    {(types || []).map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  {row.status === "pending" && !uploading && (
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0"
                      data-testid={`admin-pdf-remove-${idx}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                  {row.status === "uploading" && (
                    <CircleNotch size={18} className="text-primary animate-spin shrink-0" />
                  )}
                  {row.status === "done" && (
                    <CheckCircle size={18} weight="fill" className="text-green-500 shrink-0" />
                  )}
                  {row.status === "error" && (
                    <span
                      className="text-xs font-nunito text-red-600 shrink-0"
                      title={row.error}
                    >
                      Failed
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Shared fields */}
          <div>
            <label className="font-nunito text-sm font-bold text-foreground mb-1 block">
              Attach all to Product (optional)
            </label>
            <select
              value={shared.product_id}
              onChange={(e) => setShared({ ...shared, product_id: e.target.value })}
              className="w-full font-nunito border-2 border-foreground rounded-xl px-3 py-2 bg-white"
              data-testid="admin-pdf-product"
            >
              <option value="">— Standalone (no bundle) —</option>
              {(products || []).map((prod) => (
                <option key={prod.id} value={prod.id}>{prod.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-nunito text-sm font-bold text-foreground mb-1 block">
              Topic (shared)
            </label>
            <select
              value={shared.topic}
              onChange={(e) => setShared({ ...shared, topic: e.target.value })}
              className="w-full font-nunito border-2 border-foreground rounded-xl px-3 py-2 bg-white"
              data-testid="admin-pdf-topic"
            >
              {topics.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-nunito text-sm font-bold text-foreground mb-2 block">
              Grades (shared) — pick all that apply
            </label>
            <div className="flex flex-wrap gap-2">
              {grades.map((g) => {
                const active = shared.grades.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGrade(g)}
                    className={`px-4 py-2 rounded-full border-2 border-foreground font-nunito font-bold text-sm transition-colors ${
                      active
                        ? "bg-primary text-white"
                        : "bg-white text-foreground hover:bg-muted"
                    }`}
                    data-testid={`admin-pdf-grade-${g}`}
                    aria-pressed={active}
                  >
                    {g === "K" ? "K" : `Grade ${g}`}
                  </button>
                );
              })}
            </div>
          </div>

          <Textarea
            placeholder="Description (shared, optional)"
            value={shared.description}
            onChange={(e) => setShared({ ...shared, description: e.target.value })}
            className="font-nunito border-2 border-foreground rounded-xl"
            rows={2}
            data-testid="admin-pdf-description"
          />

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 font-nunito border-2 border-foreground rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading || !rows.length}
              className="flex-1 bg-primary hover:bg-primary-hover text-white font-nunito font-bold border-2 border-foreground shadow-brutal active-brutal rounded-xl"
              data-testid="admin-pdf-upload-submit"
            >
              {uploading
                ? "Uploading…"
                : `Upload ${rows.length || ""} ${rows.length === 1 ? "PDF" : "PDFs"}`}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default PdfManager;
